import {FC, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {mixed, number, object} from 'yup';

import {Category, useUpdateCategory} from '@entities/category';
import {useUploadAdminFile} from '@entities/common';
import {getBackendAssetPath, getErrorMessageFromApiError} from '@shared/api';
import {CardBox} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {stringDefinedButCanBeEmpty} from '@shared/utils';
import {FormRow} from '@widgets/common';
import {FormFields} from '../CreateCategory/FormFields';
import {FormValues} from '../CreateCategory/v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

interface Props {
  category: Category;
  onClose: () => void;
}

const editCategoryV8nSchema = object().shape({
  slug: stringDefinedButCanBeEmpty,
  name: stringDefinedButCanBeEmpty,
  description: stringDefinedButCanBeEmpty,
  logoFile: mixed<FileWithPath>().nullable().defined(),
  bannerFile: mixed<FileWithPath>().nullable().defined(),
  order: number().integer().min(0).defined(),
});

export const EditCategoryForm: FC<Props> = ({category, onClose}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateCategory, isPending: isUpdating} =
    useUpdateCategory();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => category.nameTranslations ?? ({} as TranslationDict),
  );
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationDict>(
      () => category.descriptionTranslations ?? ({} as TranslationDict),
    );

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(editCategoryV8nSchema),
    defaultValues: {
      slug: category.id,
      name: category.name,
      description: category.description,
      logoFile: null,
      bannerFile: null,
      order: category.order,
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      const uploadedBanner = values.bannerFile
        ? await uploadAdminFile({
            file: values.bannerFile,
            type: 'category_banner',
          })
        : null;

      await updateCategory({
        slug: category.id,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        descriptionTrans: {
          ...descriptionTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            descriptionTranslations,
            values.description,
            !!dirtyFields.description,
          ),
        },
        logoUrl: uploadedLogo?.key ?? getBackendAssetPath(category.logoUrl),
        bannerUrl:
          uploadedBanner?.key ?? getBackendAssetPath(category.bannerUrl),
        order: values.order,
      });

      enqueueSnackbar('Category updated successfully', {variant: 'success'});
      onClose();
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not update category. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE} maxWidth={600}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <FormFields
            existingLogoUrl={category.logoUrl}
            existingBannerUrl={category.bannerUrl}
            nameTranslations={nameTranslations}
            onNameTranslationsChange={translations => {
              setValue('name', translations[SupportedLanguage.English]);
              setNameTranslations(translations);
            }}
            descriptionTranslations={descriptionTranslations}
            onDescriptionTranslationsChange={translations => {
              setValue('description', translations[SupportedLanguage.English]);
              setDescriptionTranslations(translations);
            }}
          />
        </FormProvider>

        <FormRow width={420} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>

          <Button
            type="submit"
            sx={{flex: 2}}
            disabled={isUpdating || isUploading}
          >
            Apply changes
          </Button>
        </FormRow>
      </form>
    </CardBox>
  );
};
